import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useCreateOutreachAttemptMutation } from '@common/cache';
import { addOutreachAttemptSchema } from '../schema';
import { useState } from 'react';
import { t } from 'i18next';
import { formatApiError } from '@common/errors/error-formatting.ts';
import { InferType } from 'yup';
import { paths } from '@common/types';
import dayjs from 'dayjs';

type FormShape = InferType<typeof addOutreachAttemptSchema>;

const initialValues: Partial<FormShape> = {
  attempt_date: new Date().toISOString(),
  new_latest_date: dayjs(new Date()).add(4, 'day').toISOString(),
  outreach_method: '',
  outreach_disposition: '',
  notes: '',
};

export const useAddOutreachAttemptModal = (task_id: string) => {
  const { mutateAsync, isPending } = useCreateOutreachAttemptMutation(task_id);

  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const formMethods = useForm<FormShape>({
    mode: 'onChange',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver<FormShape, any, any>(addOutreachAttemptSchema),
    defaultValues: initialValues,
  });

  const outreachMethodOptions = [
    'Phone Call',
    'In Person',
    'Email',
    'Text Message',
    'Letter',
  ].map(o => ({
    value: o,
    label: o,
  }));

  const outreachDispositionOptions = [
    'Left voicemail',
    'Line busy',
    'No answer',
    'Disconnected',
    'Wrong number',
    'Number not in service',
    'Do Not Call requested',
    'Callback requested',
    'Referred to email',
  ].map(o => ({
    value: o,
    label: o,
  }));

  const { handleSubmit, reset, getValues } = formMethods;

  const submitHandler = async (values: FormShape) => {
    try {
      await mutateAsync({
        ...values,
        notes: values.notes ?? '',
        outreach_method:
          values.outreach_method as paths['/task/{task_id}/outreach']['post']['requestBody']['content']['application/json']['outreach_method'],
        outreach_disposition:
          values.outreach_disposition as paths['/task/{task_id}/outreach']['post']['requestBody']['content']['application/json']['outreach_disposition'],
        events: [],
      });

      toast.success(t('worklist.addOutreachAttempt.successMessage'));
      setIsOpen(false);
      reset(initialValues);
    } catch (error) {
      toast.error(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const submitDateValue = getValues('attempt_date');

  return {
    onSubmit,
    formMethods,
    isOpen,
    handleOpen,
    handleClose,
    loading: isPending,
    outreachDispositionOptions,
    outreachMethodOptions,
    submitDateValue,
  };
};
